import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Zap, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'vpm_exit_popup_shown';
const INACTIVITY_MS = 30_000;

const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isVideoPlaying = useCallback(() => {
    const videos = document.querySelectorAll('video');
    for (const v of videos) {
      if (!v.paused && !v.ended) return true;
    }
    if (document.activeElement?.tagName === 'IFRAME') return true;
    return false;
  }, []);

  const alreadyShown = useCallback(() => {
    return shownRef.current || sessionStorage.getItem(STORAGE_KEY) === 'true';
  }, []);

  const showPopup = useCallback(() => {
    if (alreadyShown() || isVideoPlaying()) return;
    shownRef.current = true;
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setOpen(true);
  }, [alreadyShown, isVideoPlaying]);

  // Exit intent (mouse leaves viewport top)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.clientY <= 0) showPopup();
    };
    document.addEventListener('mouseout', handler);
    return () => document.removeEventListener('mouseout', handler);
  }, [showPopup]);

  // Inactivity timer
  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        showPopup();
      }, INACTIVITY_MS);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [showPopup]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md border-accent/30 bg-background">
        <DialogHeader className="text-center items-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-2 animate-pulse-glow">
            <Zap className="w-7 h-7 text-primary" />
          </div>
          <DialogTitle className="font-display text-xl sm:text-2xl text-foreground">
            Before You Go — Take a Quick Quiz
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm mt-1">
            Discover your <span className="font-bold text-primary">Content System Score</span> in 60 seconds. Find out what's holding your content back and get a free growth roadmap.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <Button
            variant="hero"
            size="lg"
            className="w-full"
            onClick={() => {
              setOpen(false);
              window.location.href = '/quiz';
            }}
          >
            Take the Quiz <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <p className="text-xs text-center text-muted-foreground">Free • 60 seconds • No signup required to start</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
